import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { LocationDetails } from '@rma/public/locations/public/api-location-details';
import { BehaviorSubject, map, Observable, take, tap } from 'rxjs';
import { ShortlistResult } from '../domain/shortlist';
import { ApiShortlistStorageService } from './api-shortlist-storage.service';
import { LocalShortlistStorageService } from './local-shortlist-storage.service';
import { ShortlistStorageService } from './shortlist-storage.service';

export interface CompareAgent {
  shortlistCount$: Observable<number>;
  shortlist$: Observable<string[]>;
  location: LocationDetails | undefined;
  remove(agentCode: string): Observable<ShortlistResult>;
}

@Injectable({
  providedIn: 'root',
})
export class ShortlistService implements CompareAgent {
  private readonly userService = inject(PublicUserContextService);
  private readonly localShortlistStorage = inject(LocalShortlistStorageService);
  private readonly apiShortlistStorage = inject(ApiShortlistStorageService);
  private readonly shortlistAgentCodesSource = new BehaviorSubject<string[]>([]);
  private readonly destroyRef = inject(DestroyRef);

  public readonly shortlistCount$: Observable<number>;
  public readonly shortlist$: Observable<string[]>;
  public readonly location = undefined;

  private shortListStorage: ShortlistStorageService;

  public constructor() {
    this.shortlist$ = this.shortlistAgentCodesSource.asObservable();
    this.shortlistCount$ = this.shortlist$.pipe(map((x) => x.length));

    this.shortListStorage = this.localShortlistStorage;

    this.userService.isAuthenticated$.pipe(takeUntilDestroyed()).subscribe((isAuthenticated) => {
      this.shortListStorage = isAuthenticated ? this.apiShortlistStorage : this.localShortlistStorage;
      this.replace(this.shortListStorage.load());
    });
  }

  public remove(agentCode: string): Observable<ShortlistResult> {
    return this.shortListStorage.remove(agentCode).pipe(
      tap((result) => {
        if (result) {
          this.shortlist = this.shortlist.filter((code) => code !== agentCode);
        }
      }),
    );
  }

  public toggleShortList(agentCode: string): Observable<ShortlistResult> {
    const isShortlisted = this.shortlist.some((code) => code === agentCode);
    if (isShortlisted) {
      return this.remove(agentCode);
    } else {
      return this.add(agentCode);
    }
  }

  public addMany(agentCodes: string[]): Observable<ShortlistResult[]> {
    return this.shortListStorage.addMany(agentCodes).pipe(
      tap((result) => {
        const newAgentCodes = result.filter(({ success }) => !!success).map(({ agentCode }) => agentCode);

        if (newAgentCodes.length) {
          this.shortlist = [...this.shortlist, ...newAgentCodes];
        }
      }),
    );
  }

  private replace(agentCodes$: Observable<string[]>) {
    agentCodes$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((agentCodes) => (this.shortlist = agentCodes));
  }

  private add(agentCode: string): Observable<ShortlistResult> {
    return this.shortListStorage.add(agentCode).pipe(
      tap((result) => {
        if (result.success) this.shortlist = [...this.shortlist, agentCode];
      }),
    );
  }

  private get shortlist(): string[] {
    return this.shortlistAgentCodesSource.getValue();
  }

  private set shortlist(val: string[]) {
    this.shortlistAgentCodesSource.next(val);
  }
}
